import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type PromptRow = { id: string; tags: string[] | null };
const norm = (s: string) => s.trim().toLowerCase();

export const usePromptTags = (workspaceId: string, open: boolean) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<PromptRow[]>([]);
  const [phantoms, setPhantoms] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("composer_user_prompts")
        .select("id, tags")
        .eq("workspace_id", workspaceId);
      if (error) throw new Error(error.message);
      setRows((data ?? []) as PromptRow[]);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  const tagCounts = useMemo(() => {
    const m = new Map<string, number>();
    const display = new Map<string, string>();
    for (const r of rows) {
      for (const raw of r.tags ?? []) {
        const t = (raw ?? "").trim();
        if (!t) continue;
        const k = t.toLowerCase();
        m.set(k, (m.get(k) ?? 0) + 1);
        if (!display.has(k)) display.set(k, t);
      }
    }
    return Array.from(m.entries())
      .map(([k, count]) => ({ key: k, name: display.get(k) ?? k, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const allItems = useMemo(() => {
    const seen = new Set(tagCounts.map((t) => t.key));
    const phantomItems = phantoms
      .filter((p) => !seen.has(p.toLowerCase()))
      .map((p) => ({ key: p.toLowerCase(), name: p, count: 0 }));
    return [...tagCounts, ...phantomItems].sort((a, b) => a.name.localeCompare(b.name));
  }, [tagCounts, phantoms]);

  const updateRowsForTag = async (
    targetKey: string,
    transform: (tags: string[]) => string[],
  ) => {
    const affected = rows.filter((r) =>
      (r.tags ?? []).some((t) => norm(t) === targetKey),
    );
    if (affected.length === 0) return;
    const updates = affected.map((r) => {
      const next = transform(r.tags ?? []);
      return supabase.from("composer_user_prompts").update({ tags: next }).eq("id", r.id);
    });
    const results = await Promise.all(updates);
    for (const res of results) {
      if (res.error) throw new Error(res.error.message);
    }
  };

  const deleteTags = async (keys: string[]) => {
    setBusy(true);
    try {
      for (const k of keys) {
        await updateRowsForTag(k, (tags) => tags.filter((t) => norm(t) !== k));
      }
      setPhantoms((p) => p.filter((t) => !keys.includes(t.toLowerCase())));
      toast.success(`Deleted ${keys.length} tag${keys.length === 1 ? "" : "s"}`);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const renameTag = async (oldKey: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error("Tag name cannot be empty");
      return false;
    }
    if (trimmed.toLowerCase() === oldKey) return true;
    setBusy(true);
    try {
      await updateRowsForTag(oldKey, (tags) => {
        const filtered = tags.filter((t) => norm(t) !== oldKey);
        if (filtered.some((t) => norm(t) === trimmed.toLowerCase())) return filtered;
        return [...filtered, trimmed];
      });
      setPhantoms((p) => p.filter((t) => t.toLowerCase() !== oldKey));
      toast.success("Tag renamed");
      await refresh();
      return true;
    } catch (e) {
      toast.error((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addPhantom = (value: string) => {
    const v = value.trim();
    if (!v) return false;
    const k = v.toLowerCase();
    if (allItems.some((i) => i.key === k)) {
      toast.message("Tag already exists");
      return false;
    }
    setPhantoms((p) => [...p, v]);
    return true;
  };

  const resetPhantoms = () => setPhantoms([]);

  return { loading, busy, allItems, refresh, deleteTags, renameTag, addPhantom, resetPhantoms };
};
